import React, { useEffect, useMemo, useState } from "react";
import AceEditor from "../../../../utils/aceEditor";
import "ace-builds/src-noconflict/mode-sql";
import "ace-builds/src-noconflict/theme-dreamweaver";
import authService from "../../../../utils/authService";
import type { CodingPreviewModel } from "./questionPreviewAdapter";
import "./SQLEditor.css";

type Props = { model: CodingPreviewModel };

type TestCaseResult = { id: string; passed: boolean; detail?: string };

type TableRow = { tab_name: string; data: Record<string, unknown>[] };

/** Pixel-faithful SQLCodeEditorComponent layout (testing context: RUN CODE only). */
const SqlCodingPreview: React.FC<Props> = ({ model }) => {
  const [code, setCode] = useState(model.template || model.answer);
  const [running, setRunning] = useState(false);
  const [tablesLoading, setTablesLoading] = useState(false);
  const [availableTables, setAvailableTables] = useState<TableRow[]>([]);
  const [successMessage, setSuccessMessage] = useState("");
  const [additionalMessage, setAdditionalMessage] = useState("");
  const [results, setResults] = useState<TestCaseResult[]>([]);
  const [sqlRows, setSqlRows] = useState<Record<string, unknown>[]>([]);
  const [sqlError, setSqlError] = useState("");
  const [sqlLeftTab, setSqlLeftTab] = useState<"table" | "output">("table");
  const [selectedTable, setSelectedTable] = useState("");

  const questionTableNames = useMemo(() => {
    if (!model.table) return [];
    return model.table
      .split(",")
      .map((name) => name.trim())
      .filter(Boolean);
  }, [model.table]);

  // Same as student: prefer question.Tables, else fetch global tables API
  useEffect(() => {
    let cancelled = false;

    const loadTables = async () => {
      setCode(model.template || model.answer);
      setSuccessMessage("");
      setAdditionalMessage("");
      setResults([]);
      setSqlRows([]);
      setSqlError("");
      setSqlLeftTab("table");

      if (model.tables.length > 0) {
        setAvailableTables(model.tables);
        const first =
          questionTableNames[0] || model.tables[0]?.tab_name || "";
        setSelectedTable(first);
        return;
      }

      setTablesLoading(true);
      try {
        const response = await authService.get("content_creation/tables-data/");
        if (cancelled) return;
        const raw = response.data?.tables || response.data?.data?.tables || {};
        let list: TableRow[] = [];
        if (Array.isArray(raw)) {
          list = raw
            .map((item: { tab_name?: string; name?: string; data?: unknown }) => ({
              tab_name: String(item.tab_name || item.name || ""),
              data: Array.isArray(item.data)
                ? (item.data as Record<string, unknown>[])
                : [],
            }))
            .filter((t: TableRow) => t.tab_name);
        } else if (raw && typeof raw === "object") {
          list = Object.entries(raw as Record<string, unknown>).map(
            ([name, data]) => ({
              tab_name: name,
              data: Array.isArray(data)
                ? (data as Record<string, unknown>[])
                : [],
            })
          );
        }
        setAvailableTables(list);
        const first =
          questionTableNames[0] ||
          list.find((t) =>
            questionTableNames.some(
              (n) => n.toLowerCase() === t.tab_name.toLowerCase()
            )
          )?.tab_name ||
          list[0]?.tab_name ||
          "";
        setSelectedTable(first);
      } catch (err) {
        console.error("Failed to load tables-data:", err);
        if (!cancelled) {
          setAvailableTables([]);
          setSelectedTable(questionTableNames[0] || "");
        }
      } finally {
        if (!cancelled) setTablesLoading(false);
      }
    };

    loadTables();
    return () => {
      cancelled = true;
    };
  }, [model, questionTableNames]);

  const tablesToUse = useMemo(() => {
    if (model.tables.length > 0) return model.tables;
    return availableTables;
  }, [model.tables, availableTables]);

  const tableNames = useMemo(() => {
    if (questionTableNames.length > 0) return questionTableNames;
    return tablesToUse.map((t) => t.tab_name);
  }, [questionTableNames, tablesToUse]);

  const selectedTableData = tablesToUse.find(
    (t) => t.tab_name.toLowerCase() === selectedTable.toLowerCase()
  );

  const handleRun = async () => {
    setRunning(true);
    setSuccessMessage("");
    setAdditionalMessage("");
    setResults([]);
    setSqlRows([]);
    setSqlError("");
    try {
      const response = await authService.post(
        "content_creation/execute-query/",
        { query: code }
      );
      const rows = response.data?.data || response.data?.rows || [];
      const list = Array.isArray(rows) ? rows : [];
      if (list[0]?.error) {
        setSqlError(String(list[0].error));
        setSqlRows([]);
        setResults([
          { id: "TestCase1", passed: false, detail: "Failed" },
          { id: "Result", passed: false, detail: "False" },
        ]);
        setSuccessMessage("Wrong Answer");
        setAdditionalMessage("You have not passed the test cases");
      } else {
        setSqlRows(list);
        const expected = model.expectedOutput || [];
        const passed =
          expected.length === 0 ||
          JSON.stringify(list) === JSON.stringify(expected);
        setResults([
          {
            id: "TestCase1",
            passed,
            detail: passed ? "Passed" : "Failed",
          },
          { id: "Result", passed, detail: passed ? "True" : "False" },
        ]);
        if (passed) {
          setSuccessMessage("Congratulations!");
          setAdditionalMessage(
            "You have passed the test cases."
          );
        } else {
          setSuccessMessage("Wrong Answer");
          setAdditionalMessage("You have not passed the test cases");
        }
      }
    } catch (err: unknown) {
      console.error(err);
      setSqlError(
        (err as { response?: { data?: { detail?: string } }; message?: string })
          ?.response?.data?.detail ||
          (err as { message?: string })?.message ||
          "Could not execute SQL via content_creation/execute-query/."
      );
      setResults([
        { id: "TestCase1", passed: false, detail: "Failed" },
        { id: "Result", passed: false, detail: "False" },
      ]);
      setSuccessMessage("Error");
      setAdditionalMessage("Network or API error occurred");
    } finally {
      setRunning(false);
    }
  };

  return (
    <div
      className="d-flex"
      style={{ height: "70vh", width: "100%", maxHeight: "70vh" }}
    >
      <div
        className="bg-white"
        style={{
          width: "45%",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          marginRight: "10px",
          flexShrink: 0,
        }}
      >
        <div
          className="bg-white"
          style={{ height: "45%", backgroundColor: "#E5E5E533" }}
        >
          <div
            className="p-3"
            style={{ height: "100%", overflowY: "auto", overflowX: "hidden" }}
          >
            <p style={{ wordWrap: "break-word", whiteSpace: "pre-wrap" }}>
              Question ID : {model.question_id}
            </p>
            <p style={{ wordWrap: "break-word", whiteSpace: "pre-wrap" }}>
              {model.prompt}
            </p>
          </div>
        </div>
        <div
          className="bg-white sql-preview-left-bottom"
          style={{
            height: "50%",
            backgroundColor: "#E5E5E533",
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
          }}
        >
          <div
            className="d-flex flex-nowrap align-items-center mt-2 mb-2 mx-3 gap-2"
            style={{ fontSize: "12px", flexShrink: 0 }}
          >
            <button
              type="button"
              className="nav-link me-0"
              onClick={() => setSqlLeftTab("table")}
              style={{
                boxShadow: "#888 1px 2px 5px 0px",
                backgroundColor:
                  sqlLeftTab === "table" ? "black" : "transparent",
                color: sqlLeftTab === "table" ? "white" : "black",
                whiteSpace: "nowrap",
                border: "none",
                borderRadius: "0.375rem",
                padding: "6px 12px",
              }}
            >
              Table
            </button>
            <button
              type="button"
              className="nav-link"
              onClick={() => setSqlLeftTab("output")}
              style={{
                boxShadow: "#888 1px 2px 5px 0px",
                backgroundColor:
                  sqlLeftTab === "output" ? "black" : "transparent",
                color: sqlLeftTab === "output" ? "white" : "black",
                whiteSpace: "nowrap",
                border: "none",
                borderRadius: "0.375rem",
                padding: "6px 12px",
              }}
            >
              Expected Output
            </button>
          </div>
          <div
            className="tab-content"
            style={{
              flex: 1,
              minHeight: 0,
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
            }}
          >
            {sqlLeftTab === "table" && (
              <div
                role="tabpanel"
                className="ms-3"
                style={{
                  flex: 1,
                  minHeight: 0,
                  display: "flex",
                  flexDirection: "column",
                  overflow: "hidden",
                }}
              >
                <div
                  className="d-flex flex-wrap"
                  style={{ fontSize: "12px", flexShrink: 0 }}
                >
                  {tableNames.map((tableName) => {
                    const isActive =
                      selectedTable.toLowerCase() === tableName.toLowerCase();
                    return (
                      <button
                        key={tableName}
                        type="button"
                        className="me-2 mb-2"
                        onClick={() => setSelectedTable(tableName)}
                        style={{
                          fontSize: "12px",
                          boxShadow: "#888 1px 2px 5px 0px",
                          backgroundColor: isActive ? "black" : "transparent",
                          color: isActive ? "white" : "black",
                          whiteSpace: "nowrap",
                          border: "none",
                          borderRadius: "0.375rem",
                          padding: "6px 12px",
                        }}
                      >
                        {tableName}
                      </button>
                    );
                  })}
                </div>
                <div
                  className="sql-table-container me-3 mb-2"
                  style={{
                    flex: 1,
                    overflowY: "auto",
                    overflowX: "auto",
                    minHeight: 0,
                  }}
                >
                  {tablesLoading ? (
                    <div className="small text-muted p-2">Loading tables...</div>
                  ) : selectedTable &&
                    selectedTableData?.data &&
                    selectedTableData.data.length > 0 ? (
                    <table
                      className="table table-bordered table-sm mb-0"
                      style={{ width: "auto", fontSize: "12px" }}
                    >
                      <thead>
                        <tr>
                          {Object.keys(selectedTableData.data[0] || {}).map(
                            (header) => (
                              <th key={header} className="text-center">
                                {header}
                              </th>
                            )
                          )}
                        </tr>
                      </thead>
                      <tbody>
                        {selectedTableData.data.map((row, index) => (
                          <tr key={index}>
                            {Object.keys(row || {}).map((header) => (
                              <td
                                key={header}
                                className="text-center"
                                style={{
                                  whiteSpace: "nowrap",
                                  padding: "5px",
                                }}
                              >
                                {String(row[header] ?? "")}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <div className="small text-muted p-2">
                      {selectedTable
                        ? `No data for table "${selectedTable}".`
                        : "No tables available for this question."}
                    </div>
                  )}
                </div>
              </div>
            )}
            {sqlLeftTab === "output" && (
              <div
                role="tabpanel"
                className="ms-3 me-3 mb-2 table-responsive"
                style={{
                  flex: 1,
                  minHeight: 0,
                  overflow: "auto",
                  fontSize: "12px",
                }}
              >
                {model.expectedOutput.length > 0 && model.expectedOutput[0] ? (
                  <table
                    className="table table-bordered table-sm"
                    style={{ width: "auto", fontSize: "12px" }}
                  >
                    <thead>
                      <tr>
                        {Object.keys(model.expectedOutput[0] || {}).map(
                          (header) => (
                            <th key={header} className="text-center">
                              {header}
                            </th>
                          )
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {model.expectedOutput.map((row, index) => (
                        <tr key={index}>
                          {Object.keys(row || {}).map((header) => (
                            <td
                              key={header}
                              className="text-center"
                              style={{
                                whiteSpace: "nowrap",
                                padding: "5px",
                              }}
                            >
                              {String(row[header] ?? "")}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div className="small text-muted p-2">
                    No expected output for this question.
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      <div
        className="bg-white"
        style={{
          flex: 1,
          height: "100%",
          display: "flex",
          flexDirection: "column",
          minWidth: 0,
        }}
      >
        <div
          className="bg-white"
          style={{ height: "45%", backgroundColor: "#E5E5E533", padding: "10px" }}
        >
          <AceEditor
            mode="sql"
            theme="dreamweaver"
            onChange={setCode}
            value={code}
            fontSize={14}
            placeholder="Write all SQL commands/clauses in UPPERCASE"
            showPrintMargin={false}
            showGutter={false}
            highlightActiveLine={false}
            wrapEnabled={true}
            style={{ width: "100%", height: "100%", margin: "0px" }}
            setOptions={{ useWorker: false }}
          />
        </div>

        <div
          style={{ height: "6%", backgroundColor: "#E5E5E533" }}
          className="d-flex flex-column justify-content-center processingDivStyle"
        >
          <div className="d-flex justify-content-between align-items-center h-100">
            <div className="d-flex flex-column justify-content-center">
              {running ? (
                <h5 className="m-0 processingDivHeadingTag">Processing...</h5>
              ) : (
                <>
                  {successMessage && (
                    <h5 className="m-0 ps-1" style={{ fontSize: "14px" }}>
                      {successMessage}
                    </h5>
                  )}
                  {additionalMessage && (
                    <p
                      className="processingDivParaTag m-0 ps-1"
                      style={{ fontSize: "10px" }}
                    >
                      {additionalMessage}
                    </p>
                  )}
                </>
              )}
            </div>
            <div className="d-flex justify-content-end align-items-center">
              <button
                type="button"
                className="btn btn-sm btn-light me-2 processingDivButton"
                style={{
                  whiteSpace: "nowrap",
                  fontSize: "12px",
                  minWidth: "70px",
                  boxShadow: "#888 1px 2px 5px 0px",
                  height: "30px",
                }}
                onClick={handleRun}
                disabled={running}
              >
                RUN CODE
              </button>
            </div>
          </div>
        </div>

        <div
          className="bg-white"
          style={{
            height: "49%",
            backgroundColor: "#E5E5E533",
            position: "relative",
          }}
        >
          <div
            className="p-3"
            style={{ height: "100%", overflowY: "auto", overflowX: "auto" }}
          >
            {sqlError ? (
              <div className="pe-2">
                <div
                  className="alert alert-danger w-auto mx-auto"
                  style={{
                    fontSize: "12px",
                    wordWrap: "break-word",
                    whiteSpace: "pre-wrap",
                    overflowWrap: "break-word",
                  }}
                >
                  <strong>Error:</strong> {sqlError}
                </div>
              </div>
            ) : (
              sqlRows.length > 0 &&
              sqlRows[0] && (
                <div
                  className="table-responsive"
                  style={{ width: "100%", overflowX: "auto" }}
                >
                  <table
                    className="table table-bordered table-sm rounded"
                    style={{ width: "auto", fontSize: "12px" }}
                  >
                    <thead>
                      <tr>
                        {Object.keys(sqlRows[0] || {}).map((header) => (
                          <th
                            key={header}
                            className="text-center"
                            style={{ whiteSpace: "nowrap", padding: "5px" }}
                          >
                            {header}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {sqlRows.map((row, index) => (
                        <tr key={index}>
                          {Object.keys(row || {}).map((header) => (
                            <td
                              key={header}
                              className="text-center"
                              style={{ whiteSpace: "nowrap", padding: "5px" }}
                            >
                              {String(row[header] ?? "")}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )
            )}
            <div className="mt-3">
              {results.map((testCase) => (
                <div
                  key={testCase.id}
                  className="d-flex align-items-center mb-2 border border-ligth shadow bg-white p-2 rounded-2"
                  style={{ width: "fit-content", fontSize: "12px" }}
                >
                  <span className="me-2">{testCase.id}:</span>
                  <span
                    style={{
                      color:
                        testCase.detail === "Passed" ||
                        testCase.detail === "True" ||
                        testCase.passed
                          ? "blue"
                          : "red",
                    }}
                  >
                    {testCase.detail ||
                      (testCase.passed ? "Passed" : "Failed")}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SqlCodingPreview;
